import React, { useEffect, useState, useMemo } from 'react';
import { Table, Card, App, Tabs, Row, Col, Statistic, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useAuth0 } from '@auth0/auth0-react';
import { theme } from '../../../theme';
import { FileTextOutlined, ShoppingOutlined, BarChartOutlined, DatabaseOutlined, DollarOutlined } from '@ant-design/icons';

const { Option } = Select;

interface PendienteOrdenesItem {
  id: number;
  id_insumo: number;
  id_padron: number;
  orden: string;
  insumo_descripcion: string;
  padron: string;
  cantidad: number;
  cantidad_facturada: number;
  fecha_entrega: string;
  tasaiva: number;
  precio_unitario: number;
  subtotal: number;
  iva: number;
  iinterno: number;
  movimientos_recepcion: number;
}

const ListarPendienteOrdenes: React.FC = () => {
  const { message: messageApi } = App.useApp();
  const { getAccessTokenSilently, user } = useAuth0();
  const [data, setData] = useState<PendienteOrdenesItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroProveedor, setFiltroProveedor] = useState<string>('todos');
  const [filtroInsumo, setFiltroInsumo] = useState<string>('todos');

  const empresa = user?.id_empresa;
  const sucursal = user?.id_sucursal;

  // Calcular filtros usando useMemo para evitar errores cuando data no es un array
  const padronFilters = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];
    return Array.from(new Set(data.map(item => item.padron))).map(padron => ({
      text: padron,
      value: padron,
    }));
  }, [data]);

  const insumoFilters = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];
    return Array.from(new Set(data.map(item => item.insumo_descripcion))).map(insumo => ({
      text: insumo,
      value: insumo,
    }));
  }, [data]);

  const columns: ColumnsType<PendienteOrdenesItem> = useMemo(() => [
    {
      title: 'Orden',
      dataIndex: 'orden',
      key: 'orden',
      align: 'right',
      sorter: (a, b) => a.orden.localeCompare(b.orden),
    },
    {
      title: 'Fecha Entrega',
      dataIndex: 'fecha_entrega',
      key: 'fecha_entrega',
      align: 'right',
      sorter: (a, b) => a.fecha_entrega.localeCompare(b.fecha_entrega),
    },
    {
      title: 'Proveedor',
      dataIndex: 'padron',
      key: 'padron',
      sorter: (a, b) => a.padron.localeCompare(b.padron),
      filterSearch: true,
      filters: padronFilters,
      onFilter: (value, record) => record.padron === value,
    },
    {
      title: 'Insumo',
      dataIndex: 'insumo_descripcion',
      key: 'insumo_descripcion',
      sorter: (a, b) => a.insumo_descripcion.localeCompare(b.insumo_descripcion),
      filterSearch: true,
      filters: insumoFilters,
      onFilter: (value, record) => record.insumo_descripcion === value,
    },
    {
      title: 'Cantidad Entregada',
      dataIndex: 'cantidad',
      key: 'cantidad',
      align: 'right',
      sorter: (a, b) => a.cantidad - b.cantidad,
    },
    {
      title: 'Cantidad Facturada',
      dataIndex: 'cantidad_facturada',
      key: 'cantidad_facturada',
      align: 'right',
      sorter: (a, b) => a.cantidad_facturada - b.cantidad_facturada,
    },
    {
      title: 'Precio',
      dataIndex: 'precio_unitario',
      key: 'precio_unitario',
      align: 'right',
      sorter: (a, b) => a.precio_unitario - b.precio_unitario,
    },
    {
      title: 'Subtotal',
      dataIndex: 'subtotal',
      key: 'subtotal',
      align: 'right',
      sorter: (a, b) => a.subtotal - b.subtotal,
    }
  ], [padronFilters, insumoFilters]);

  const fetchData = async () => {
    if (!empresa || !sucursal) {
      messageApi.error('Faltan datos necesarios para la consulta');
      return;
    }

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_INSUMOS_URL}/pendiente_ocompra_uno/${empresa}/${sucursal}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Error al obtener datos');
      }

      const responseData = await response.json();
      // Manejar diferentes estructuras de respuesta
      let dataArray: PendienteOrdenesItem[] = [];
      
      if (Array.isArray(responseData)) {
        // Si la respuesta es directamente un array
        dataArray = responseData;
      } else if (responseData && Array.isArray(responseData.data)) {
        // Si la respuesta es un objeto con propiedad 'data' que es un array
        dataArray = responseData.data;
      } else if (responseData && responseData.message) {
        // Si hay un mensaje pero no hay datos, establecer array vacío
        console.log('Mensaje del servidor:', responseData.message);
        dataArray = [];
      } else {
        // Caso por defecto: array vacío
        console.warn('Estructura de respuesta no reconocida:', responseData);
        dataArray = [];
      }
      
      setData(dataArray);
    } catch (error) {
      messageApi.error('Error al cargar los datos');
      console.error('Error:', error);
      setData([]); // Asegurar que data siempre sea un array
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (empresa && sucursal) {
      fetchData();
    }
  }, [empresa, sucursal]);

  // Cálculo de estadísticas
  const estadisticas = useMemo(() => {
    // Filtrar datos según filtros seleccionados
    let datosFiltrados = data;
    if (filtroProveedor !== 'todos') {
      datosFiltrados = datosFiltrados.filter(item => item.padron === filtroProveedor);
    }
    if (filtroInsumo !== 'todos') {
      datosFiltrados = datosFiltrados.filter(item => item.insumo_descripcion === filtroInsumo);
    }

    const totalRegistros = datosFiltrados.length;
    const totalCantidad = datosFiltrados.reduce((sum, item) => sum + (item.cantidad || 0), 0);
    const totalCantidadFacturada = datosFiltrados.reduce((sum, item) => sum + (item.cantidad_facturada || 0), 0);
    const cantidadPendiente = totalCantidad - totalCantidadFacturada;
    const totalSubtotal = datosFiltrados.reduce((sum, item) => sum + (item.subtotal || 0), 0);
    const promedioSubtotal = totalRegistros > 0 ? totalSubtotal / totalRegistros : 0;
    
    // Distribución por proveedor
    const porProveedor = datosFiltrados.reduce((acc, item) => {
      const proveedor = item.padron || 'Sin proveedor';
      if (!acc[proveedor]) {
        acc[proveedor] = { cantidad: 0, totalCantidad: 0, totalSubtotal: 0 };
      }
      acc[proveedor].cantidad += 1;
      acc[proveedor].totalCantidad += item.cantidad || 0;
      acc[proveedor].totalSubtotal += item.subtotal || 0;
      return acc;
    }, {} as Record<string, { cantidad: number; totalCantidad: number; totalSubtotal: number }>);
    
    const distribucionProveedor = Object.entries(porProveedor)
      .map(([proveedor, datos]) => ({
        proveedor,
        cantidad: datos.cantidad,
        totalCantidad: datos.totalCantidad,
        totalSubtotal: datos.totalSubtotal,
        porcentaje: totalCantidad > 0 ? (datos.totalCantidad / totalCantidad) * 100 : 0
      }))
      .sort((a, b) => b.totalSubtotal - a.totalSubtotal)
      .slice(0, 10);
    
    // Distribución por insumo
    const porInsumo = datosFiltrados.reduce((acc, item) => {
      const insumo = item.insumo_descripcion || 'Sin insumo';
      if (!acc[insumo]) {
        acc[insumo] = { cantidad: 0, totalCantidad: 0, totalSubtotal: 0 };
      }
      acc[insumo].cantidad += 1;
      acc[insumo].totalCantidad += item.cantidad || 0;
      acc[insumo].totalSubtotal += item.subtotal || 0;
      return acc;
    }, {} as Record<string, { cantidad: number; totalCantidad: number; totalSubtotal: number }>);
    
    const distribucionInsumo = Object.entries(porInsumo)
      .map(([insumo, datos]) => ({
        insumo,
        cantidad: datos.cantidad,
        totalCantidad: datos.totalCantidad,
        totalSubtotal: datos.totalSubtotal,
        porcentaje: totalCantidad > 0 ? (datos.totalCantidad / totalCantidad) * 100 : 0
      }))
      .sort((a, b) => b.totalSubtotal - a.totalSubtotal)
      .slice(0, 10);
    
    // Distribución por orden
    const porOrden = datosFiltrados.reduce((acc, item) => {
      const orden = item.orden || 'Sin orden';
      if (!acc[orden]) {
        acc[orden] = { cantidad: 0, totalCantidad: 0, totalSubtotal: 0 };
      }
      acc[orden].cantidad += 1;
      acc[orden].totalCantidad += item.cantidad || 0;
      acc[orden].totalSubtotal += item.subtotal || 0;
      return acc;
    }, {} as Record<string, { cantidad: number; totalCantidad: number; totalSubtotal: number }>);
    
    const distribucionOrden = Object.entries(porOrden)
      .map(([orden, datos]) => ({
        orden,
        cantidad: datos.cantidad,
        totalCantidad: datos.totalCantidad,
        totalSubtotal: datos.totalSubtotal,
        porcentaje: totalCantidad > 0 ? (datos.totalCantidad / totalCantidad) * 100 : 0
      }))
      .sort((a, b) => b.totalSubtotal - a.totalSubtotal)
      .slice(0, 10);
    
    const proveedoresUnicos = Array.from(new Set(data.map(item => item.padron))).sort();
    const insumosUnicos = Array.from(new Set(data.map(item => item.insumo_descripcion))).sort();
    
    return {
      totalRegistros,
      totalCantidad,
      totalCantidadFacturada,
      cantidadPendiente,
      totalSubtotal,
      promedioSubtotal,
      distribucionProveedor,
      distribucionInsumo,
      distribucionOrden,
      proveedoresUnicos,
      insumosUnicos
    };
  }, [data, filtroProveedor, filtroInsumo]);

  return (
    <div style={{ padding: '0' }}>
      {/* Título de la página */}
      <h1 style={{ 
        color: theme.colors.primaryDark, 
        fontSize: '28px', 
        fontWeight: 600, 
        marginBottom: '24px' 
      }}>
        Pendientes de Órdenes
      </h1>

      <Card 
        style={{ 
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow,
          maxWidth: '100%'
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '16px',
            fontWeight: 600
          }
        }}
        title="Informe de Órdenes de Compra Pendientes"
      >
        <Tabs
          defaultActiveKey="listado"
          size="small"
          items={[
            {
              key: 'listado',
              label: 'Listado',
              children: (
                <Table
                  columns={columns}
                  dataSource={data}
                  loading={loading}
                  scroll={{ x: 800 }}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} registros`,
                  }}
                />
              )
            },
            {
              key: 'estadisticas',
              label: 'Estadísticas',
              children: (
                <div style={{ padding: '20px 0' }}>
                  {/* Filtros */}
                  <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    <Col xs={24} sm={12} lg={6}>
                      <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 500 }}>Filtrar por Proveedor:</div>
                      <Select
                        value={filtroProveedor}
                        onChange={setFiltroProveedor}
                        size="small"
                        style={{ width: '100%' }}
                        placeholder="Todos los proveedores"
                      >
                        <Option value="todos">Todos</Option>
                        {estadisticas.proveedoresUnicos.map(prov => (
                          <Option key={prov} value={prov}>{prov}</Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 500 }}>Filtrar por Insumo:</div>
                      <Select
                        value={filtroInsumo}
                        onChange={setFiltroInsumo}
                        size="small"
                        style={{ width: '100%' }}
                        placeholder="Todos los insumos"
                      >
                        <Option value="todos">Todos</Option>
                        {estadisticas.insumosUnicos.map(ins => (
                          <Option key={ins} value={ins}>{ins}</Option>
                        ))}
                      </Select>
                    </Col>
                  </Row>

                  {/* Tarjetas de resumen */}
                  <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    <Col xs={24} sm={12} lg={6}>
                      <Card>
                        <Statistic
                          title="Total de Registros"
                          value={estadisticas.totalRegistros}
                          prefix={<FileTextOutlined />}
                          valueStyle={{ color: theme.colors.primary }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Card>
                        <Statistic
                          title="Total Cantidad"
                          value={estadisticas.totalCantidad}
                          prefix={<ShoppingOutlined />}
                          valueStyle={{ color: theme.colors.success }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Card>
                        <Statistic
                          title="Cantidad Pendiente"
                          value={estadisticas.cantidadPendiente}
                          prefix={<BarChartOutlined />}
                          valueStyle={{ color: theme.colors.warning }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Card>
                        <Statistic
                          title="Total Subtotal"
                          value={estadisticas.totalSubtotal}
                          prefix={<DollarOutlined />}
                          precision={2}
                          valueStyle={{ color: theme.colors.info }}
                        />
                      </Card>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    <Col xs={24} sm={12} lg={6}>
                      <Card>
                        <Statistic
                          title="Promedio Subtotal"
                          value={estadisticas.promedioSubtotal}
                          prefix={<DollarOutlined />}
                          precision={2}
                          valueStyle={{ color: theme.colors.primary }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Card>
                        <Statistic
                          title="Cantidad Facturada"
                          value={estadisticas.totalCantidadFacturada}
                          prefix={<DatabaseOutlined />}
                          valueStyle={{ color: theme.colors.success }}
                        />
                      </Card>
                    </Col>
                  </Row>

                  {/* Distribución por Proveedor */}
                  <Card 
                    title="Top 10 - Distribución por Proveedor" 
                    style={{ marginBottom: '24px' }}
                  >
                    <Table
                      dataSource={estadisticas.distribucionProveedor}
                      rowKey="proveedor"
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: 'Proveedor',
                          dataIndex: 'proveedor',
                          key: 'proveedor',
                        },
                        {
                          title: 'Cantidad Registros',
                          dataIndex: 'cantidad',
                          key: 'cantidad',
                          align: 'right' as const,
                        },
                        {
                          title: 'Total Cantidad',
                          dataIndex: 'totalCantidad',
                          key: 'totalCantidad',
                          align: 'right' as const,
                          render: (valor: number) => valor.toLocaleString('es-AR'),
                        },
                        {
                          title: 'Total Subtotal',
                          dataIndex: 'totalSubtotal',
                          key: 'totalSubtotal',
                          align: 'right' as const,
                          render: (valor: number) => `$${valor.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        },
                        {
                          title: 'Porcentaje',
                          dataIndex: 'porcentaje',
                          key: 'porcentaje',
                          align: 'right' as const,
                          render: (valor: number) => `${valor.toFixed(1)}%`,
                        },
                      ]}
                    />
                  </Card>

                  {/* Distribución por Insumo */}
                  <Card 
                    title="Top 10 - Distribución por Insumo" 
                    style={{ marginBottom: '24px' }}
                  >
                    <Table
                      dataSource={estadisticas.distribucionInsumo}
                      rowKey="insumo"
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: 'Insumo',
                          dataIndex: 'insumo',
                          key: 'insumo',
                        },
                        {
                          title: 'Cantidad Registros',
                          dataIndex: 'cantidad',
                          key: 'cantidad',
                          align: 'right' as const,
                        },
                        {
                          title: 'Total Cantidad',
                          dataIndex: 'totalCantidad',
                          key: 'totalCantidad',
                          align: 'right' as const,
                          render: (valor: number) => valor.toLocaleString('es-AR'),
                        },
                        {
                          title: 'Total Subtotal',
                          dataIndex: 'totalSubtotal',
                          key: 'totalSubtotal',
                          align: 'right' as const,
                          render: (valor: number) => `$${valor.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        },
                        {
                          title: 'Porcentaje',
                          dataIndex: 'porcentaje',
                          key: 'porcentaje',
                          align: 'right' as const,
                          render: (valor: number) => `${valor.toFixed(1)}%`,
                        },
                      ]}
                    />
                  </Card>

                  {/* Distribución por Orden */}
                  <Card 
                    title="Top 10 - Distribución por Orden" 
                    style={{ marginBottom: '24px' }}
                  >
                    <Table
                      dataSource={estadisticas.distribucionOrden}
                      rowKey="orden"
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: 'Orden',
                          dataIndex: 'orden',
                          key: 'orden',
                        },
                        {
                          title: 'Cantidad Registros',
                          dataIndex: 'cantidad',
                          key: 'cantidad',
                          align: 'right' as const,
                        },
                        {
                          title: 'Total Cantidad',
                          dataIndex: 'totalCantidad',
                          key: 'totalCantidad',
                          align: 'right' as const,
                          render: (valor: number) => valor.toLocaleString('es-AR'),
                        },
                        {
                          title: 'Total Subtotal',
                          dataIndex: 'totalSubtotal',
                          key: 'totalSubtotal',
                          align: 'right' as const,
                          render: (valor: number) => `$${valor.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        },
                        {
                          title: 'Porcentaje',
                          dataIndex: 'porcentaje',
                          key: 'porcentaje',
                          align: 'right' as const,
                          render: (valor: number) => `${valor.toFixed(1)}%`,
                        },
                      ]}
                    />
                  </Card>
                </div>
              )
            }
          ]}
        />
      </Card>
    </div>
  );
};

export default ListarPendienteOrdenes;
